"use client";

import { useEffect, useMemo, useState } from "react";
import { useRankHistory } from "../hooks";
import {
  RANGE_OPTIONS,
  FOLLOWERS_RANGE_OPTIONS,
  CHART_HEIGHT_DESKTOP,
  CHART_HEIGHT_MOBILE,
  useChartOptions,
  RangeSelector,
  ChartContainer,
  useIsMobile,
  buildChartEventMarkers,
} from "@/components/shared/rank-history";
import type { RankListType } from "../types";
import { RANK_LIST_CONFIGS } from "../types";
import { pickBucketSeconds } from "../api";
import { useIntersectionObserver } from "@/hooks";
import { useGameEvents } from "@/features/games/hooks/useGameEvents";
import { RegionSelect } from "@/components/shared/RegionSelect";
import { useChartScreenshotPeriod } from "@/features/games/details/components/ChartBlockWithScreenshot";
import {
  earliestTs,
  type HistoryExtent,
} from "@/components/shared/rank-history/historyExtent";

interface RankHistoryProps {
  type: RankListType;
  appId: number;
  title: string;
  description: string;
  /** Без зовнішньої картки — для вбудовування в блок із скріншотом */
  embedded?: boolean;
  /** Контрольований регіон (Top Sellers): якщо передано разом з onCcChange — режим controlled */
  cc?: string;
  onCcChange?: (cc: string) => void;
  /** Дата релізу гри для маркера на графіку */
  releaseDate?: string | null;
  /** Дата виходу в Early Access для маркера на графіку */
  eaReleaseDate?: string | null;
  /** Timestamp першого потрапляння в топ-селлери (тільки для type=topsellers) */
  firstTopSellersTs?: number | null;
  /**
   * Release moment (epoch s). A rank exists only while the game is on the
   * list, so the series is sparse; the release is what bounds it for the
   * history banner. Without it the banner follows the plan alone.
   */
  releaseTs?: number | null;
  /** Останній ранг з графіку (останній запис за часом) — для відображення як "rank now" */
  onLastRankFromChart?: (rank: number | null) => void;
  /** Додаткова дія під графіком (напр. кнопка регіональної матриці) */
  footerAction?: React.ReactNode;
}

export function RankHistory({
  type,
  appId,
  title,
  description,
  embedded,
  cc: controlledCc,
  onCcChange,
  releaseDate,
  eaReleaseDate,
  firstTopSellersTs,
  releaseTs,
  onLastRankFromChart,
  footerAction,
}: RankHistoryProps) {
  const { elementRef, isVisible } = useIntersectionObserver({
    rootMargin: "200px", // Починає завантажувати за 200px до появи
  });

  const config = RANK_LIST_CONFIGS[type];
  const isFollowers = type === "followers";
  const isTopSellers = type === "topsellers";
  const isNewTrending = type === "newtrending";
  const rangeOptions = isFollowers ? FOLLOWERS_RANGE_OPTIONS : RANGE_OPTIONS;
  const defaultRangeKey = isFollowers ? "7d" : "48h";
  const [selectedRangeKey, setSelectedRangeKey] =
    useState<string>(defaultRangeKey);
  const [internalCc, setInternalCc] = useState<string>("global");
  const isCcControlled = controlledCc !== undefined && onCcChange != null;
  const selectedCc = isCcControlled ? controlledCc : internalCc;
  const setSelectedCc = isCcControlled ? onCcChange : setInternalCc;
  const [timeAnchor, setTimeAnchor] = useState<number>(() =>
    Math.floor(Date.now() / 1000)
  );

  const to = timeAnchor;
  const selectedRange =
    rangeOptions.find((option) => option.key === selectedRangeKey) ??
    rangeOptions[0];
  const from = selectedRange.seconds
    ? Math.max(0, to - selectedRange.seconds)
    : 0;

  const { history, isLoading, error } = useRankHistory({
    type,
    appId,
    from,
    to,
    cc: isTopSellers || isNewTrending ? selectedCc : undefined,
    enabled: Boolean(appId) && isVisible, // Завантажуємо тільки коли видимий
  });

  const chartScreenshot = useChartScreenshotPeriod();
  useEffect(() => {
    chartScreenshot?.setPeriod(selectedRangeKey);
  }, [selectedRangeKey, chartScreenshot]);

  useEffect(() => {
    if (!onLastRankFromChart) return;
    const withRank = history.filter((p) => p.rank != null);
    if (withRank.length === 0) {
      onLastRankFromChart(null);
      return;
    }
    const last = [...withRank].sort((a, b) => b.timestamp - a.timestamp)[0];
    onLastRankFromChart(last?.rank ?? null);
  }, [history, onLastRankFromChart]);

  const chartData = useMemo(
    () =>
      history
        .filter((point) => point.rank !== null)
        .map(
          (point) => [point.timestamp * 1000, point.rank!] as [number, number]
        ),
    [history]
  );

  const historyExtent = useMemo<HistoryExtent>(
    () => ({
      from,
      to,
      firstPointTs: earliestTs(
        history.map((point) => (point.rank !== null ? point.timestamp : null)),
      ),
      kind: "sparse",
      releaseTs,
    }),
    [history, from, to, releaseTs],
  );

  const isMobile = useIsMobile();
  const eventMarkers = useMemo(
    () =>
      buildChartEventMarkers({
        releaseDate,
        eaReleaseDate,
        firstTopSellersTs: isTopSellers ? firstTopSellersTs : undefined,
        chartType: isFollowers ? "followers" : "topsellers",
      }),
    [releaseDate, eaReleaseDate, firstTopSellersTs, isTopSellers, isFollowers]
  );
  const { festivalMarkers } = useGameEvents({ appId, enabled: isVisible });
  // Бакет ≥ 1 день → таймстемпи в даних — це початок дня (час завжди 00:00),
  // тож на чарті показуємо лише дату без часу.
  const rangeSeconds =
    from > 0 ? Math.max(0, to - from) : Number.MAX_SAFE_INTEGER;
  const dayOnly = pickBucketSeconds(rangeSeconds) >= 24 * 60 * 60;
  const chartOptions = useChartOptions(
    chartData,
    isMobile,
    config.primaryColor,
    isFollowers,
    eventMarkers,
    undefined,
    festivalMarkers,
    dayOnly
  );
  const chartHeight = isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_DESKTOP;

  const handleRangeChange = (key: string) => {
    setSelectedRangeKey(key);
  };

  const handleRefresh = () => {
    setTimeAnchor(Math.floor(Date.now() / 1000));
  };

  const inner = (
    <>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-white">
            {title}
          </h3>
          <p className="text-[#8f98a0] text-xs md:text-sm">{description}</p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {(isTopSellers || isNewTrending) && (
            <RegionSelect
              chart={type}
              value={selectedCc}
              onChange={(cc) => setSelectedCc(cc)}
              excludeGlobal={isNewTrending}
            />
          )}

          <RangeSelector
            chart={type}
            selectedRangeKey={selectedRangeKey}
            onRangeChange={handleRangeChange}
            onRefresh={handleRefresh}
            isLoading={isLoading}
            isMobile={isMobile}
            primaryColor={config.primaryColor}
            rangeOptions={rangeOptions}
          />
        </div>
      </div>

      <ChartContainer
            chart={type}
        chartOptions={chartOptions}
        isLoading={isLoading}
        error={error}
        hasData={history.length > 0}
        height={chartHeight}
        primaryColor={config.primaryColor}
        festivalMarkers={festivalMarkers}
        footerAction={footerAction}
        historyExtent={historyExtent}
      />
    </>
  );

  if (embedded) {
    return (
      <div ref={elementRef} className="space-y-3 md:space-y-4">
        {inner}
      </div>
    );
  }

  return (
    <div
      ref={elementRef}
      className="bg-[#16202d] rounded-lg border border-[#2a475e] p-3 md:p-6 space-y-3 md:space-y-4"
    >
      {inner}
    </div>
  );
}
